import * as types from "../types/actionTypes";
import objectAssign from "object-assign";
import fetch from "node-fetch";
import utils from "./../../universal/utils";
import * as cookieStorage from "./common/cookieStorage";
import { ContactsOutlined } from "@mui/icons-material";
import { normalizeLocationId } from "../../components/lobby/lobbyPageUtils";
import { toast } from "../../universal/toast";
import {
  UNVERIFIED_ADD_FRIEND_MESSAGE,
  UNVERIFIED_TARGET_ADD_FRIEND_MESSAGE,
} from "../../utils/friendRequests";
import { hasExplicitLogout } from "../../universal/explicitLogoutGuard";
import { getPendingAccountProfileLeadConversion, trackRedditAccountProfile } from "../../universal/redditPixel";
import { getStoredAttribution } from "../../universal/campaignAttribution";

const cStorage = cookieStorage.getCookieStorage();
const basePath = utils.getEdgeBasePath();
const { fetchJsonWithDiagnostics } = utils;

let matchPollingPidForMatchData = null;
let matchPollingMatchId = null;
let matchPollingHasLoaded = false;
let matchPollingMissStartedAt = 0;
let matchPollingIntervalMs = 0;
let matchPollingDispatch = null;
let matchPollingPidForScoreboard = null;
let matchPollingMatchIdForScoreboard = null;
let friendRequestsPollingPid = null;
let latestMatchesPollingPid = null;
let latestMatchesPollingStatusFilter = "all";

const STEAM_QUEUE_BLOCK_REASONS = new Set([
  "missing_steam_id",
  "cs2_hours_not_public",
  "valve_ban_check_unavailable",
  "vac_banned",
]);

function isSteamQueueBlockResponse(resp) {
  return resp?.steamQueueBlocked === true || STEAM_QUEUE_BLOCK_REASONS.has(resp?.reason);
}

const MATCH_PAGE_FAST_POLL_MS = 800;
const MATCH_PAGE_SLOW_POLL_MS = 5000;
const MATCH_PAGE_MISSING_AFTER_MS = 12000;

function stopMatchDataPolling() {
  if (matchPollingPidForMatchData) {
    clearInterval(matchPollingPidForMatchData);
    matchPollingPidForMatchData = null;
  }
  matchPollingMatchId = null;
  matchPollingHasLoaded = false;
  matchPollingMissStartedAt = 0;
  matchPollingIntervalMs = 0;
  matchPollingDispatch = null;
}

function startMatchDataPollingLoop() {
  if (matchPollingPidForMatchData) {
    clearInterval(matchPollingPidForMatchData);
    matchPollingPidForMatchData = null;
  }
  if (!matchPollingMatchId || !matchPollingDispatch) return;
  matchPollingPidForMatchData = setInterval(() => {
    if (matchPollingMatchId && matchPollingDispatch) {
      getMatchDataById(matchPollingMatchId, matchPollingDispatch);
    }
  }, matchPollingIntervalMs);
}

function stopScoreboardPolling() {
  if (matchPollingPidForScoreboard) {
    clearInterval(matchPollingPidForScoreboard);
    matchPollingPidForScoreboard = null;
  }
  matchPollingMatchIdForScoreboard = null;
}

function stopLatestMatchesPollingTimer() {
  if (latestMatchesPollingPid) {
    clearInterval(latestMatchesPollingPid);
    latestMatchesPollingPid = null;
  }
}

export function stopLatestMatchesPolling() {
  return () => {
    stopLatestMatchesPollingTimer();
  };
}

export function setUser({ user, clearAccountDraft = false }) {
  const action = {
    type: types.LOAD_USER_PROFILE,
    user,
  };
  if (clearAccountDraft) {
    action.clearAccountDraft = true;
  }
  return action;
}

export function updateDisplayPresence(displayPresence) {
  return {
    type: types.UPDATE_DISPLAY_PRESENCE,
    displayPresence,
  };
}

async function fetchAuthenticatedSessionUser() {
  const sessionResult = await fetch(`${basePath}/auth/session`, {
    method: "GET",
    credentials: "include",
    mode: "cors",
    cache: "no-store",
  });
  const sessionResponse = await sessionResult.json().catch(() => null);

  if (!sessionResult.ok || !sessionResponse?.user) {
    return null;
  }

  return sessionResponse.user;
}

export function refreshSessionUser(options = {}) {
  return async function (dispatch) {
    try {
      const refreshedUser = await fetchAuthenticatedSessionUser();
      if (hasExplicitLogout()) {
        return null;
      }
      if (refreshedUser) {
        dispatch(
          setUser({
            user: refreshedUser,
            clearAccountDraft: options?.clearAccountDraft === true,
          })
        );
      }
      return refreshedUser;
    } catch (_) {
      return null;
    }
  };
}

export function getMapsSelectionFromServer({ userId }) {
  return function (dispatch) {
    fetch(`${basePath}/api/v1/lobby/maps/${userId}`, {
      credentials: "include",
    })
      .then(function (apiResult) {
        if (!apiResult.ok) {
          console.error('[getMapsSelectionFromServer] API returned error:', apiResult.status);
          return null;
        }
        return apiResult.json();
      })
      .then(function (result) {
        if (!result) {
          console.warn('[getMapsSelectionFromServer] No result from API');
          return;
        }
        
        dispatch({
          type: types.LOBBY_MAPS_UPDATE_BULK,
          maps: result.maps,
          wingmanMaps: result.wingmanMaps,
          matchmakingOptions: result.matchmakingOptions,
          steamResult: result.steamResult,
        });
        if (Array.isArray(result.selectedServerLocations)) {
          dispatch(setSelectedServerLocations(result.selectedServerLocations));
        }
      })
      .catch(function (error) {
        console.error('[getMapsSelectionFromServer] Failed to fetch maps:', error);
      });
  };
}

export function updateMapsBulkOnStartup(mapsSelected) {
  return {
    type: types.LOBBY_MAPS_UPDATE_BULK,
    mapsSelected,
  };
}

export function showMatchFoundNotification(
    matchId,
    timeoutSeconds,
    map,
    totalPlayers,
    {
      expiresAt = null,
      hasAccepted = false,
      acceptedCount = 0,
      allAccepted = false,
      slots = null,
      partyAcceptedUserIds = null,
      location = null,
      mapVotePending = false,
      teams = null,
    } = {}
) {
  return {
    type: types.LOBBY_MATCH_FOUND_NOTIFY,
    matchId,
    timeoutSeconds,
    map,
    totalPlayers,
    expiresAt,
    hasAccepted,
    acceptedCount,
    allAccepted,
    slots,
    partyAcceptedUserIds,
    location,
    mapVotePending: Boolean(mapVotePending),
    teams,
  };
}

export function closeMatchAcceptDialog() {
  return {
    type: types.CLOSE_MATCH_ACCEPT_DIALOG,
  };
}

export function lobbyAcceptMatch({ matchId }) {
  return {
    type: types.LOBBY_ACCEPT_MATCH,
    matchId,
  };
}

export function updateMatchAcceptanceStatus({
  matchId,
  acceptedCount,
  totalPlayers,
  allAccepted,
  hasAccepted,
  expiresAt,
  slots,
  partyAcceptedUserIds,
  mapVotePending,
  map,
  teams,
}) {
  return {
    type: types.UPDATE_MATCH_ACCEPTANCE_STATUS,
    matchId,
    acceptedCount,
    totalPlayers,
    allAccepted,
    hasAccepted,
    expiresAt,
    slots,
    partyAcceptedUserIds,
    mapVotePending,
    map,
    teams,
  };
}

export function matchAcceptanceCancelled({
  matchId,
  outcome,
  reason,
  playerQueueActionStartTs,
} = {}) {
  return {
    type: types.MATCH_ACCEPTANCE_CANCELLED,
    matchId,
    outcome,
    reason,
    playerQueueActionStartTs,
  };
}

export function startMapVote({
  matchId,
  candidates,
  blockedMaps,
  expiresAt,
  durationSeconds,
  voters,
  votes,
  myVote,
  teamStats,
}) {
  return {
    type: types.START_MAP_VOTE,
    matchId,
    candidates,
    blockedMaps,
    expiresAt,
    durationSeconds,
    voters,
    votes,
    myVote,
    teamStats,
  };
}

export function updateMapVote({
  matchId,
  candidates,
  blockedMaps,
  expiresAt,
  durationSeconds,
  voters,
  votes,
  myVote,
  teamStats,
}) {
  return {
    type: types.UPDATE_MAP_VOTE,
    matchId,
    candidates,
    blockedMaps,
    expiresAt,
    durationSeconds,
    voters,
    votes,
    myVote,
    teamStats,
  };
}

export function resolveMapVote({
  matchId,
  map,
  tally,
  votes,
  voters,
  teamStats,
  wasTie,
  tieBrokenBy,
  tiedMaps,
  resolvedBy,
  topCount,
}) {
  return {
    type: types.RESOLVE_MAP_VOTE,
    matchId,
    map,
    tally,
    votes,
    voters,
    teamStats,
    wasTie,
    tieBrokenBy,
    tiedMaps,
    resolvedBy,
    topCount,
  };
}

export function closeMapVote() {
  return {
    type: types.CLOSE_MAP_VOTE,
  };
}

export function castMapVoteLocally({ matchId, map }) {
  return {
    type: types.CAST_MAP_VOTE_LOCALLY,
    matchId,
    map,
  };
}

export function lobbyStopSearch({ userId } = {}) {
  return function (dispatch) {
    const clearLocalQueueState = () => {
      dispatch({
        type: types.LOBBY_PLAYER_STOP_SEARCH,
      });
    };

    if (!userId) {
      clearLocalQueueState();
      return Promise.resolve(false);
    }

    return fetch(`${basePath}/api/v1/matchmaking/remove/user/from/pool/${userId}`, {
      method: "POST",
      credentials: "include",
      body: JSON.stringify({ userId }),
      headers: { "Content-Type": "application/json" },
    })
      .then(function (apiResult) {
        return apiResult
          .json()
          .catch(() => ({}))
          .then(function (resp) {
            // Always clear local searching UI; server self-heals leftover
            // mm_queue rows on the next enqueue if remove failed.
            clearLocalQueueState();
            if (!apiResult.ok || resp?.ok === false) {
              console.warn("[lobbyStopSearch] dequeue rejected:", resp);
              return false;
            }
            return true;
          });
      })
      .catch((err) => {
        console.error("[lobbyStopSearch] dequeue failed:", err);
        clearLocalQueueState();
        return false;
      });
  };
}

export function lobbyAddPlayerToQueuePool({ userId, queueMode }) {
  const nowUnix = Math.floor(new Date().getTime() / 1000);
  const playerQueueActionStartTs = nowUnix;
  return function (dispatch) {
    // Enter the searching view immediately. The enqueue endpoint performs
    // several authoritative eligibility and queue checks, so waiting for its
    // response here makes normal network/backend latency feel like an
    // unresponsive PLAY button. Any rejection below rolls this state back.
    dispatch({
      type: types.LOBBY_PLAYER_SEARCHING_FOR_MATCH_START,
      playerQueueActionStartTs,
    });

    const attemptEnqueue = () =>
      fetch(`${basePath}/api/v1/matchmaking/add/user/to/pool/${userId}`, {
        method: "POST",
        credentials: "include",
        body: JSON.stringify({ userId, playerQueueActionStartTs, queueMode }),
        headers: { "Content-Type": "application/json" },
      }).then(function (apiResult) {
        return apiResult
          .json()
          .catch(() => ({}))
          .then(function (resp) {
            return { apiResult, resp };
          });
      });

    const rejectEnqueue = (resp) => {
      console.warn("[lobbyAddPlayerToQueuePool] enqueue rejected:", resp);
      dispatch({ type: types.LOBBY_PLAYER_STOP_SEARCH });
      dispatch({ type: types.CLEAR_QUEUE_STATUS });

      if (isSteamQueueBlockResponse(resp)) {
        dispatch({
          type: types.SHOW_QUEUE_BLOCKED_MODAL,
          reason: resp?.reason || null,
          message: resp.message,
          blockedMembers: resp?.blockedMembers,
        });
        return false;
      }

      if (resp?.message) {
        dispatch({
          type: types.SHOW_QUEUE_ERROR_MESSAGE,
          message: resp.message,
        });
        setTimeout(() => dispatch({ type: types.HIDE_QUEUE_ERROR_MESSAGE }), 5000);
      }
      return false;
    };

    const isAlreadyQueueingError = (resp) => {
      const detail = String(resp?.err || resp?.txt || resp?.message || "");
      return /already queueing/i.test(detail);
    };

    return attemptEnqueue()
      .then(async function ({ apiResult, resp }) {
        if (apiResult.ok && resp?.ok !== false) {
          return playerQueueActionStartTs;
        }

        // One self-heal retry: cancel leftover server queue state then re-add.
        if (isAlreadyQueueingError(resp) && userId) {
          try {
            await fetch(
              `${basePath}/api/v1/matchmaking/remove/user/from/pool/${userId}`,
              {
                method: "POST",
                credentials: "include",
                body: JSON.stringify({ userId }),
                headers: { "Content-Type": "application/json" },
              }
            );
          } catch (cleanupErr) {
            console.warn(
              "[lobbyAddPlayerToQueuePool] stale-queue cleanup failed:",
              cleanupErr
            );
          }

          const retry = await attemptEnqueue();
          if (retry.apiResult.ok && retry.resp?.ok !== false) {
            return playerQueueActionStartTs;
          }
          return rejectEnqueue(retry.resp);
        }

        return rejectEnqueue(resp);
      })
      .catch((err) => {
        console.error("[lobbyAddPlayerToQueuePool] enqueue failed:", err);
        dispatch({ type: types.LOBBY_PLAYER_STOP_SEARCH });
        dispatch({ type: types.CLEAR_QUEUE_STATUS });
        dispatch({
          type: types.SHOW_QUEUE_ERROR_MESSAGE,
          message: "Failed to join matchmaking queue. Please try again.",
        });
        setTimeout(() => dispatch({ type: types.HIDE_QUEUE_ERROR_MESSAGE }), 5000);
        return false;
      });
  };
}

export function lobbyExpandSearch({ userId }) {
  return function (dispatch) {
    fetch(`${basePath}/api/v1/matchmaking/expand-search/${userId}`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
    })
      .then((r) => r.json().catch(() => ({})))
      .then((resp) => {
        if (resp?.ok) {
          dispatch({ type: types.LOBBY_EXPAND_SEARCH });
        }
      })
      .catch((err) => console.error("[lobbyExpandSearch]", err));
  };
}

export function lobbySmallerMatchOptIn({ userId }) {
  return function (dispatch) {
    return fetch(`${basePath}/api/v1/matchmaking/smaller-match/opt-in/${userId}`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
    })
      .then((r) => r.json().catch(() => ({})))
      .then((resp) => {
        if (resp?.ok) {
          dispatch({ type: types.LOBBY_SMALLER_MATCH_STATUS, status: "opted_in" });
          return true;
        }
        if (resp?.message) {
          dispatch(showQueueErrorMessage(resp.message));
        }
        return false;
      })
      .catch((err) => {
        console.error("[lobbySmallerMatchOptIn]", err);
        return false;
      });
  };
}

export function lobbySmallerMatchDecline({ userId }) {
  return function (dispatch) {
    return fetch(`${basePath}/api/v1/matchmaking/smaller-match/decline/${userId}`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
    })
      .then((r) => r.json().catch(() => ({})))
      .then((resp) => {
        if (resp?.ok) {
          dispatch({ type: types.LOBBY_SMALLER_MATCH_STATUS, status: "declined" });
          return true;
        }
        if (resp?.message) {
          dispatch(showQueueErrorMessage(resp.message));
        }
        return false;
      })
      .catch((err) => {
        console.error("[lobbySmallerMatchDecline]", err);
        return false;
      });
  };
}

// NEW UNIFIED FUNCTION for matchmaking options (Player-specific settings)
export function lobbyMatchmakingOptionChanged({ name, enabled, value, hasSlider, listValue, userId }) {
  return function (dispatch) {
    dispatch({
      type: types.LOBBY_MATCHMAKING_OPTION_CHANGED,
      name,
      enabled,
      value,
      hasSlider,
      listValue,
    });

    fetch(`${basePath}/api/v1/lobby/matchmaking/option/changed/${userId}`, {
      method: "POST",
      credentials: "include",
      body: JSON.stringify({ name, enabled, value, hasSlider, listValue }),
      headers: { "Content-Type": "application/json" },
    }).then(function (apiResult) {
      return apiResult.json().then(function (resp) {
        console.log("Matchmaking option updated:", resp);
      });
    }).catch(function(error) {
      console.error("Error updating matchmaking option:", error);
    });
  };
}

export function lobbyLeaderMatchmakingOptionChanged({ name, enabled, value, hasSlider, listValue }) {
  return {
    type: types.LOBBY_LEADER_MATCHMAKING_OPTION_CHANGED,
    name,
    enabled,
    value,
    hasSlider,
    listValue,
  };
}

export function lobbyLeaderUpdatedOptions({ lobbyCode, lobbyOptions }) {
  return {
    type: types.LOBBY_LEADER_UPDATED_OPTIONS,
    lobbyCode,
    lobbyOptions,
  };
}

export function lobbyMapClicked({ name, userId, queueMode }) {
  console.log("lobbyMapClicked", name, userId, queueMode);
  return function (dispatch) {
    fetch(`${basePath}/api/v1/lobby/map/clicked/${userId}`, {
      method: "POST",
      credentials: "include",
      body: JSON.stringify({ name, queueMode }),
      headers: { "Content-Type": "application/json" },
    }).then(function (apiResult) {
      return apiResult
        .json()
        .catch(() => ({}))
        .then(function (resp) {
          if (!apiResult.ok || resp?.ok === false) {
            if (resp?.message) {
              toast.error(resp.message, { durationMs: 5000 });
            }
            return;
          }

          dispatch({
            type: types.LOBBY_MAP_PICK,
            name,
            queueMode,
          });
        });
    });
  };
}

export function lobbyLeaderMapToggled({ mapName, fallbackMaps = [] }) {
  return {
    type: types.LOBBY_LEADER_MAP_TOGGLED,
    mapName,
    fallbackMaps,
  };
}

export function lobbyLeaderMapsToggleAll({ maps, selectAll }) {
  return {
    type: types.LOBBY_LEADER_MAPS_TOGGLE_ALL,
    maps,
    selectAll,
  };
}

export function lobbyLeaderUpdatedMaps({ lobbyCode, lobbyMapsSelected, lobbyWingmanMapsSelected }) {
  return {
    type: types.LOBBY_LEADER_UPDATED_MAPS,
    lobbyCode,
    lobbyMapsSelected,
    lobbyWingmanMapsSelected,
  };
}

// Optimistic local switch while the lobby-queue-mode-update socket round-trip
// confirms; the LOBBY_QUEUE_MODE_UPDATED broadcast reconciles every member.
export function lobbyQueueModeSwitched({ lobbyCode, queueMode, maxPlayers }) {
  return {
    type: types.LOBBY_QUEUE_MODE_SWITCHED,
    lobbyCode,
    queueMode,
    maxPlayers,
  };
}

export function lobbyLeaderUpdatedQueueMode({
  lobbyCode,
  queueMode,
  maxPlayers,
  lobbyWingmanMapsSelected,
}) {
  return {
    type: types.LOBBY_QUEUE_MODE_UPDATED,
    lobbyCode,
    queueMode,
    maxPlayers,
    lobbyWingmanMapsSelected,
  };
}

export function lobbyServerLocationClicked({ locationId, userId }) {
  return function (dispatch) {
    fetch(`${basePath}/api/v1/lobby/server-location/clicked/${userId}`, {
      method: "POST",
      credentials: "include",
      body: JSON.stringify({ locationId }),
      headers: { "Content-Type": "application/json" },
    }).then(function (apiResult) {
      return apiResult
        .json()
        .catch(() => ({}))
        .then(function (resp) {
          if (!apiResult.ok || resp?.ok === false) {
            if (resp?.message) {
              dispatch({
                type: types.SHOW_QUEUE_ERROR_MESSAGE,
                message: resp.message,
              });
              setTimeout(() => dispatch({ type: types.HIDE_QUEUE_ERROR_MESSAGE }), 5000);
            }
            return;
          }

          dispatch({
            type: types.SET_SELECTED_SERVER_LOCATIONS,
            locations: resp.locations,
          });
        });
    });
  };
}

export function toggleAllServerLocationsSelection({
  locationIds,
  userId,
  allSelected,
}) {
  return function (dispatch) {
    fetch(`${basePath}/api/v1/lobby/server-locations/toggle-all/${userId}`, {
      method: "POST",
      credentials: "include",
      body: JSON.stringify({ locationIds, allSelected }),
      headers: { "Content-Type": "application/json" },
    }).then(function (apiResult) {
      return apiResult
        .json()
        .catch(() => ({}))
        .then(function (resp) {
          if (!apiResult.ok || resp?.ok === false) {
            if (resp?.message) {
              dispatch({
                type: types.SHOW_QUEUE_ERROR_MESSAGE,
                message: resp.message,
              });
              setTimeout(
                () => dispatch({ type: types.HIDE_QUEUE_ERROR_MESSAGE }),
                5000,
              );
            }
            return;
          }

          dispatch({
            type: types.SET_SELECTED_SERVER_LOCATIONS,
            locations: resp.locations,
          });
        });
    });
  };
}

export function lobbyLeaderServerLocationToggled({ locationId, fallbackLocations = [] }) {
  return {
    type: types.LOBBY_LEADER_SERVER_LOCATION_TOGGLED,
    locationId,
    fallbackLocations,
  };
}

export function lobbyLeaderUpdatedServerLocations({ lobbyCode, lobbyServerLocationsSelected }) {
  return {
    type: types.LOBBY_LEADER_UPDATED_SERVER_LOCATIONS,
    lobbyCode,
    lobbyServerLocationsSelected,
  };
}

export function toggleAllMapsSelection({ maps, userId, allSelected, queueMode }) {
  console.log("toggleAllMapsSelection", { maps, userId, allSelected, queueMode });
  return function (dispatch) {
    fetch(`${basePath}/api/v1/lobby/maps/toggle-all/${userId}`, {
      method: "POST",
      credentials: "include",
      body: JSON.stringify({ maps, allSelected, queueMode }),
      headers: { "Content-Type": "application/json" },
    }).then(function (apiResult) {
      return apiResult
        .json()
        .catch(() => ({}))
        .then(function (result) {
          if (!apiResult.ok || result?.ok === false) {
            if (result?.message) {
              toast.error(result.message, { durationMs: 5000 });
            }
            return;
          }

          dispatch({
            type: types.LOBBY_MAPS_TOGGLE_ALL,
            maps: result.maps,
            allSelected,
            queueMode,
          });
        });
    });
  };
}

export function updateAccountFormField({ name, value }) {
  return {
    type: types.ACCOUNT_FORM_FIELD_CHANGE,
    name,
    value,
  };
}

export function dismissSuccessAccountAlert() {
  return {
    type: types.ACCOUNT_SUCCESS_ALERT_SNOOZE,
  };
}

export function setAccountUpdateAlert(text) {
  return {
    type: types.ACCOUNT_UPDATE_ALERT,
    text,
  };
}

export function dismissAccountUpdateAlert() {
  return {
    type: types.ACCOUNT_UPDATE_ALERT_SNOOZE,
  };
}

function stopFriendRequestsPollingInterval() {
  if (friendRequestsPollingPid) {
    clearInterval(friendRequestsPollingPid);
    friendRequestsPollingPid = null;
    console.log("Friend requests polling stopped");
  }
}

export function logout() {
  stopFriendRequestsPollingInterval();

  return {
    type: types.PERFORM_LOGOUT,
  };
}

export function collectMetric(metadata) {
  const stats = cStorage.getObject("stats");
  const { visitorId } = stats;

  return function (dispatch) {
    fetch(`${basePath}/collector/collect/${visitorId}`, {
      method: "POST",
      body: JSON.stringify(metadata),
      headers: { "Content-Type": "application/json" },
    }).then(function (apiResult) {
      return apiResult.json().then(function (json) {
        if (json.ok) {
          console.log("metric reported correctly");
        }
      });
    });
  };
}

export function getTrackingVisitorId() {
  const statsFromCookieStorage = cStorage.getObject("stats");

  if (statsFromCookieStorage !== null) {
    // User has an active visitorId, don't ask for one.
    return function (dispatch) {
      dispatch({
        type: types.STATS_UPDATED,
        stats: statsFromCookieStorage,
      });
    };
  }

  return function (dispatch) {
    fetch(`${basePath}/collector/visitor/id`).then(function (apiResult) {
      return apiResult.json().then(function (result) {
        const stats = {
          data: {
            likes: {
              products: [],
              shops: [],
            },
          },
          visitorId: result.visitorId,
        };

        dispatch({
          type: types.STATS_UPDATED,
          stats,
        });

        cStorage.setObject("stats", stats);
      });
    });
  };
}

export function submitAccountChanges(data) {
  return function (dispatch) {
    dispatch({
      type: types.ACCOUNT_UPDATE_DURING,
    });

    const redditConversionId = getPendingAccountProfileLeadConversion();
    const acquisition = getStoredAttribution();

    return fetch(`${basePath}/api/v1/account`, {
      body: JSON.stringify({
        data,
        ...(redditConversionId ? { redditConversionId } : {}),
        ...(acquisition ? { acquisition } : {}),
      }),
      cache: "no-cache",
      credentials: "include",
      headers: {
        "content-type": "application/json",
      },
      method: "POST",
      mode: "cors",
      redirect: "follow",
      referrer: "no-referrer",
    })
      .then(async function (apiResult) {
        const response = await apiResult.json().catch(() => null);

        if (!apiResult.ok) {
          throw new Error(
            response?.message ||
              `Account update failed with status ${apiResult.status}`
          );
        }
        let refreshedUser = null;

        try {
          refreshedUser = await refreshSessionUser()(dispatch);
        } catch (_) {
          // Best effort only. Account save already succeeded.
        }

        trackRedditAccountProfile(redditConversionId);

        return dispatch({
          type: types.ACCOUNT_UPDATE_COMPLETE,
          response: refreshedUser
            ? objectAssign({}, response, { refreshedUser })
            : response,
        });
      })
      .catch(function (error) {
        return dispatch(
          setAccountUpdateAlert(
            error?.message || "Failed to save account changes. Please try again."
          )
        );
      });
  };
}

export async function fetchSteamChecks(steamId64) {
  const res = await fetch(
    `${basePath}/api/v1/account/steam-checks?steamId64=${encodeURIComponent(steamId64)}`,
    {
      cache: "no-cache",
      credentials: "include",
      mode: "cors",
    }
  );
  const data = await res.json();
  if (!res.ok) {
    throw new Error(data?.message || "Steam checks request failed");
  }
  return data;
}

export function getProfilePageUserDataByUserId(id) {
  return function (dispatch) {
    // Set loading state to true
    dispatch({
      type: types.SET_PROFILE_DATA_LOADING,
      loading: true,
    });

    fetch(`${basePath}/api/gc/public/profile/${id}`).then(function (apiResult) {
      return apiResult.json().then(function (data) {
        if (data.ok) {
          console.log("User data found!");
          dispatch({
            type: types.GET_PROFILE_PAGE_USER_DATA,
            profileData: data.profile,
            matchSummary: data.matchSummary,
            mapsStats: data.mapsStats,
            last25MatchData: data.last25MatchData,
            recentStats: data.recentStats,
            activityMatches: data.activityMatches || [],
          });
        } else {
          dispatch({
            type: types.GET_PROFILE_PAGE_USER_DATA,
            profileData: null,
            matchSummary: null,
            mapsStats: null,
            last25MatchData: null,
            recentStats: null,
            activityMatches: [],
          });
        }
      });
    });
  }
}

export function setSortOption(sortType, value) {
  return {
    type: types.SORT_OPTION_MATCH_TABLE,
    payload: {
      sortType,
      value,
    },
  };
}

export function getMatchDataByIdWithPolling(id) {
  return (dispatch) => {
    if (matchPollingMatchId && matchPollingMatchId !== id) {
      stopMatchDataPolling();
    }

    matchPollingMatchId = id;
    matchPollingDispatch = dispatch;
    if (!matchPollingHasLoaded) {
      matchPollingIntervalMs = MATCH_PAGE_FAST_POLL_MS;
    }
    const pending = getMatchDataById(id, dispatch);

    if (!matchPollingPidForMatchData && matchPollingMatchId) {
      if (!matchPollingIntervalMs) {
        matchPollingIntervalMs = MATCH_PAGE_FAST_POLL_MS;
      }
      startMatchDataPollingLoop();
    }

    return pending;
  };
}

export function getMatchDataById(id, dispatch){
  const requestedId = id;
  return fetchJsonWithDiagnostics(
    `${basePath}/api/gc/public/matchPage/match/${id}`,
    {
      credentials: "include",
    },
    {
      context: "Match page data",
      extra: { matchId: id },
      fetchImpl: fetch,
    }
  ).then(function (result) {
    if (matchPollingMatchId !== requestedId) {
      return;
    }

    const data = result.data || {};
    const match = data.match;

    if (result.ok && data.ok && match && typeof match === "object" && match.ok !== false) {
      matchPollingHasLoaded = true;
      matchPollingMissStartedAt = 0;
      dispatch({
        type: types.GET_MATCH_DATA,
        matchFound: true,
        matchPageMissingId: null,
        matchData: match,
        scoreboardData: data.scoreboardData || null,
        matchAnalysis: data.matchAnalysis || null,
        usersMetaData: data.usersMetaData,
      });
      if (matchPollingIntervalMs !== MATCH_PAGE_SLOW_POLL_MS) {
        matchPollingIntervalMs = MATCH_PAGE_SLOW_POLL_MS;
        startMatchDataPollingLoop();
      }
      return;
    }

    // Keep last-good match data on transient errors. A failed poll used to wipe
    // the page. Fresh redirects 404 for a few seconds while the match is written,
    // so stay on loading and retry quickly until the miss is confirmed.
    const isMissing = result.status === 404;
    if (isMissing && !matchPollingHasLoaded) {
      if (!matchPollingMissStartedAt) {
        matchPollingMissStartedAt = Date.now();
      }
      if (Date.now() - matchPollingMissStartedAt >= MATCH_PAGE_MISSING_AFTER_MS) {
        dispatch({
          type: types.GET_MATCH_DATA,
          matchFound: false,
          matchPageMissingId: requestedId,
          matchData: null,
          scoreboardData: null,
          matchAnalysis: null,
          usersMetaData: null,
        });
      }
    }
  });
}

export function getScoreboardDataByMatchIdWithPolling(id) {
  return (dispatch) => {
    if (matchPollingMatchIdForScoreboard && matchPollingMatchIdForScoreboard !== id) {
      stopScoreboardPolling();
    }

    matchPollingMatchIdForScoreboard = id;
    getScoreboardDataByMatchId(id, dispatch);

    if (!matchPollingPidForScoreboard && matchPollingMatchIdForScoreboard) {
      matchPollingPidForScoreboard = setInterval(() => {
        if (matchPollingMatchIdForScoreboard) {
          getScoreboardDataByMatchId(matchPollingMatchIdForScoreboard, dispatch);
        }
      }, 5000);
    }
  };
}

export function getScoreboardDataByMatchId(id, dispatch) {
  fetch(`${basePath}/api/gc/public/match/scoreboardData/${id}`).then(function (apiResult) {
    return apiResult.json().then(function (data) {
      if (data.ok) {
        console.log("Scoreboard data taken!");
        dispatch({
          type: types.GET_SCOREBOARD_DATA,
          scoreboardData: data.scoreboardData,
        });
      } else {
        dispatch({
          type: types.GET_SCOREBOARD_DATA,
          scoreboardData: null,
        });
      }
    });
  });
}

export function sendFriendRequest({ recipientSteamId, userId, userSteamId}) {
  return (dispatch) => {
    return fetch(`${basePath}/api/v1/lobby/add-friend-request/${userId}`, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ recipientSteamId, userSteamId }),
    }).then(function (apiResult) {
      return apiResult.json().then(function (data) {
        if (data.ok) {
          console.log("Friend request sent!", data);
          dispatch({
            type: types.LOBBY_SEND_FRIEND_REQUEST_SUCCESS,
            friendRequestSuccess: "Friend request sent successfully!",
          });
          
          dispatch({
            type: types.GET_PROFILE_RELATIONSHIP,
            profileRelationship: 'pending_outgoing',
            viewerSteamId: userSteamId,
            targetSteamId: recipientSteamId,
          });
          
          setTimeout(() => {
            dispatch({
              type: types.LOBBY_CLEAR_FRIEND_REQUEST_MESSAGES,
            });
          }, 3000);
          return data;
        } else if (data.alreadyExists) {
          dispatch({
            type: types.LOBBY_SEND_FRIEND_REQUEST_ERROR,
            friendRequestError: "Friend request already exists or was already sent",
          });
          getPendingRequests(userSteamId)(dispatch);
          
          setTimeout(() => {
            dispatch({
              type: types.LOBBY_CLEAR_FRIEND_REQUEST_MESSAGES,
            });
          }, 3000);
          return data;
        } else if (data.toUserDoesNotExist) {
          dispatch({
            type: types.LOBBY_SEND_FRIEND_REQUEST_ERROR,
            friendRequestError: "User with this Steam ID does not exist",
          });
          getPendingRequests(userSteamId)(dispatch);
          
          setTimeout(() => {
            dispatch({
              type: types.LOBBY_CLEAR_FRIEND_REQUEST_MESSAGES,
            });
          }, 3000);
          return data;
        } else if (data.cannotAddSelf) {
          dispatch({
            type: types.LOBBY_SEND_FRIEND_REQUEST_ERROR,
            friendRequestError: "Cannot send friend request to yourself",
          });
          getPendingRequests(userSteamId)(dispatch);
          
          setTimeout(() => {
            dispatch({
              type: types.LOBBY_CLEAR_FRIEND_REQUEST_MESSAGES,
            });
          }, 3000);
          return data;
        } else if (data.senderNotVerified) {
          toast.warning(UNVERIFIED_ADD_FRIEND_MESSAGE, { durationMs: 4500 });
          dispatch({
            type: types.LOBBY_SEND_FRIEND_REQUEST_ERROR,
            friendRequestError: UNVERIFIED_ADD_FRIEND_MESSAGE,
          });

          setTimeout(() => {
            dispatch({
              type: types.LOBBY_CLEAR_FRIEND_REQUEST_MESSAGES,
            });
          }, 3000);
          return data;
        } else if (data.recipientNotVerified) {
          toast.warning(UNVERIFIED_TARGET_ADD_FRIEND_MESSAGE, { durationMs: 4500 });
          dispatch({
            type: types.LOBBY_SEND_FRIEND_REQUEST_ERROR,
            friendRequestError: UNVERIFIED_TARGET_ADD_FRIEND_MESSAGE,
          });

          setTimeout(() => {
            dispatch({
              type: types.LOBBY_CLEAR_FRIEND_REQUEST_MESSAGES,
            });
          }, 3000);
          return data;
        } else if (data.alreadyFriends) {
          dispatch({
            type: types.LOBBY_SEND_FRIEND_REQUEST_ERROR,
            friendRequestError: "You are already friends with this user",
          });
          getPendingRequests(userSteamId)(dispatch);
          getFriends({ userId, steamId: userSteamId })(dispatch);
          
          setTimeout(() => {
            dispatch({
              type: types.LOBBY_CLEAR_FRIEND_REQUEST_MESSAGES,
            });
          }, 3000);
          return data;
        } else {
          dispatch({
            type: types.LOBBY_SEND_FRIEND_REQUEST_ERROR,
            friendRequestError: "Error sending friend request",
          });
          getPendingRequests(userSteamId)(dispatch);
          
          setTimeout(() => {
            dispatch({
              type: types.LOBBY_CLEAR_FRIEND_REQUEST_MESSAGES,
            });
          }, 3000);
          return data;
        }
      });
    }).catch(function (error) {
      console.error("Error adding friend:", error);
      dispatch({
        type: types.LOBBY_SEND_FRIEND_REQUEST_ERROR,
        friendRequestError: "Network error. Please try again.",
      });
      getPendingRequests(userSteamId)(dispatch);
      
      setTimeout(() => {
        dispatch({
          type: types.LOBBY_CLEAR_FRIEND_REQUEST_MESSAGES,
        });
      }, 3000);
      throw error;
    });
  };
}

export function sendFriendRequestError(errorMessage) {
  return (dispatch) => {
    dispatch({
      type: types.LOBBY_SEND_FRIEND_REQUEST_ERROR,
      friendRequestError: errorMessage,
    });
    
    setTimeout(() => {
      dispatch({
        type: types.LOBBY_CLEAR_FRIEND_REQUEST_MESSAGES,
      });
    }, 3000);
  };
}

export function addPendingRequestLocally(recipientSteamId, userSteamId) {
  return (dispatch) => {
    const pendingRequest = {
      fromUser: userSteamId,
      toUser: recipientSteamId,
      createdAt: new Date(),
      status: "pending"
    };
    
    dispatch({
      type: types.ADD_PENDING_REQUEST_LOCALLY,
      pendingRequest,
    });
  };
}

export function removePendingRequestLocally(recipientSteamId) {
  return (dispatch) => {
    dispatch({
      type: types.REMOVE_PENDING_REQUEST_LOCALLY,
      recipientSteamId,
    });
  };
}

export function addFriendRequestLocally(fromUserSteamId, toUserSteamId) {
  return (dispatch) => {
    const friendRequest = {
      fromUserSteamId,
      toUserSteamId,
      createdAt: new Date(),
      status: "pending"
    };
    
    dispatch({
      type: types.ADD_FRIEND_REQUEST_LOCALLY,
      friendRequest,
    });
  };
}

export function removeFriendLocally(friendSteamId) {
  return (dispatch) => {
    dispatch({
      type: types.REMOVE_FRIEND_LOCALLY,
      friendSteamId,
    });
  };
}

export function getLeaderboard({
  scope = "global",
  country,
  limit = 50,
  offset = 0,
  append = false,
  preserveAggregatesOnLoad = false,
  includeAggregates = true,
  includeExtras = true,
} = {}) {
  return (dispatch, getState) => {
    const state = getState && getState();
    const user = state?.catalogReducer?.user;

    dispatch({
      type: types.LEADERBOARD_LOAD_START,
      scope,
      preserveAggregates: preserveAggregatesOnLoad,
    });

    let url = null;
    if (scope === "friends") {
      url = `${basePath}/api/v1/leaderboard/friends`;
    } else if (scope === "country") {
      const resolvedCountry = country || user?.country;
      if (!resolvedCountry) {
        dispatch({
          type: types.LEADERBOARD_LOAD_ERROR,
          error: "Missing country on profile",
        });
        return Promise.resolve({
          ok: false,
          error: new Error("Missing country on profile"),
        });
      }
      url = `${basePath}/api/v1/leaderboard/country/${encodeURIComponent(resolvedCountry)}`;
    } else {
      url = `${basePath}/api/v1/leaderboard/global`;
    }

    const qs = new URLSearchParams({
      limit: String(limit),
      offset: String(offset),
      includeAggregates: String(includeAggregates),
      includeExtras: String(includeExtras),
    }).toString();

    return fetch(`${url}?${qs}`, { credentials: "include" })
      .then(async (apiResult) => {
        let data = {};
        try {
          data = await apiResult.json();
        } catch (e) {
          // ignore
        }

        if (!apiResult.ok || data?.ok === false) {
          const msg =
            data?.message || data?.error || `Failed to load leaderboard (HTTP ${apiResult.status})`;
          throw new Error(msg);
        }

        dispatch({
          type: types.LEADERBOARD_LOAD_SUCCESS,
          payload: data,
          append,
        });
        return { ok: true };
      })
      .catch((err) => {
        dispatch({
          type: types.LEADERBOARD_LOAD_ERROR,
          error: err?.message || "Failed to load leaderboard",
        });
        return { ok: false, error: err };
      });
  };
}

export function leaderboardLoadError(error) {
  return {
    type: types.LEADERBOARD_LOAD_ERROR,
    error: error || "Failed to load leaderboard",
  };
}

export function getProfileRelationship({ viewerSteamId, targetSteamId }) {
  return (dispatch) => {
    return fetch(`${basePath}/api/v1/lobby/relationship/${viewerSteamId}/${targetSteamId}`)
      .then((apiResult) => apiResult.json())
      .then((data) => {
        if (data.ok) {
          dispatch({
            type: types.GET_PROFILE_RELATIONSHIP,
            profileRelationship: data.status,
            viewerSteamId,
            targetSteamId,
          });
          return data.status;
        } else {
          dispatch({
            type: types.GET_PROFILE_RELATIONSHIP,
            profileRelationship: 'none',
            viewerSteamId,
            targetSteamId,
          });
          return 'none';
        }
      })
      .catch(() => {
        dispatch({
          type: types.GET_PROFILE_RELATIONSHIP,
          profileRelationship: 'none',
          viewerSteamId,
          targetSteamId,
        });
        return 'none';
      });
  };
}

export function clearFriendRequestMessages() {
  return (dispatch) => {
    dispatch({
      type: types.LOBBY_CLEAR_FRIEND_REQUEST_MESSAGES,
    });
  };
}

export function stopFriendRequestsPolling() {
  return () => {
    stopFriendRequestsPollingInterval();
  };
}

// One-shot fetch of pending friend requests (no polling — server pushes updates via Socket.IO)
export function initFriendRequests(steamId) {
  return (dispatch) => {
    stopFriendRequestsPollingInterval();
    getFriendRequests(steamId, dispatch);
  };
}

// Backward-compat alias
export const getFriendRequestsWithPolling = initFriendRequests;

export function getFriendRequests(steamId, dispatch) {
    if (!steamId) {
      console.warn("[Lobby/API] Skipping friend requests fetch because steamId is missing");
      dispatch({
        type: types.LOBBY_GET_FRIEND_REQUESTS,
        friendRequests: [],
      });
      return;
    }

    fetchJsonWithDiagnostics(
      `${basePath}/api/v1/lobby/get-friend-requests/${steamId}`,
      {
        credentials: "include",
      },
      {
        context: "Lobby friend requests",
        extra: { steamId },
        fetchImpl: fetch,
      }
    ).then(function (result) {
      const data = result.data || {};

      if (result.ok && data.ok) {
        dispatch({
          type: types.LOBBY_GET_FRIEND_REQUESTS,
          friendRequests: data.friendRequests,
        });
      } else if (data.noFriendRequests || !result.ok) {
        dispatch({
          type: types.LOBBY_GET_FRIEND_REQUESTS,
          friendRequests: [],
        });
      }
    });
}

export function getPendingRequests(steamId){
  return (dispatch) => {
    if (!steamId) {
      console.warn("[Lobby/API] Skipping pending requests fetch because steamId is missing");
      dispatch({
        type: types.LOBBY_GET_PENDING_REQUESTS,
        pendingRequests: [],
      });
      return;
    }

    fetchJsonWithDiagnostics(
      `${basePath}/api/v1/lobby/get-pending-requests/${steamId}`,
      {
        credentials: "include",
      },
      {
        context: "Lobby pending requests",
        extra: { steamId },
        fetchImpl: fetch,
      }
    ).then(function (result) {
      const data = result.data || {};

      if (result.ok && data.ok) {
        console.log("Pending requests found!", data);
        dispatch({
          type: types.LOBBY_GET_PENDING_REQUESTS,
          pendingRequests: data.pendingRequests,
        });
      } else {
        dispatch({
          type: types.LOBBY_GET_PENDING_REQUESTS,
          pendingRequests: [],
        });
      }
    });
  };
}

export function acceptFriendRequest({ fromUserSteamId, toUserSteamId, toUserId }) {
  return (dispatch) => {
    return fetch(`${basePath}/api/v1/lobby/accept-friend-request/${toUserId}`, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ fromUserSteamId, toUserSteamId }),
    }).then(function (apiResult) {
      return apiResult.json().then(function (data) {
        if (data.ok) {
          dispatch({
            type: types.LOBBY_ACCEPT_FRIEND_REQUEST,
            friendRequestAccepted: true,
          });
          getFriendRequests(toUserSteamId, dispatch);
          getFriends({ userId: toUserId, steamId: toUserSteamId })(dispatch);
          getPendingRequests(toUserSteamId)(dispatch);
        } else {
          dispatch({
            type: types.LOBBY_ACCEPT_FRIEND_REQUEST,
            friendRequestAccepted: false,
          });
        }
        return data;
      });
    });
  };
}

export function friendRequestAccepted({ newFriend }) {
  return {
    type: types.LOBBY_FRIEND_REQUEST_ACCEPTED,
    newFriend,
  };
}

export function setFriendsFromBootstrap(friends) {
  return {
    type: types.LOBBY_GET_FRIENDS,
    friends: Array.isArray(friends) ? friends.filter((f) => !!f?.friendData) : [],
  };
}

export function setFriendRequestsFromBootstrap(friendRequests) {
  return {
    type: types.LOBBY_GET_FRIEND_REQUESTS,
    friendRequests: Array.isArray(friendRequests) ? friendRequests : [],
  };
}

export function declineFriendRequest({ fromUserSteamId, toUserSteamId, toUserId }) {
  return (dispatch) => {
    return fetch(`${basePath}/api/v1/lobby/decline-friend-request/${toUserId}`, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      }, 
      body: JSON.stringify({ fromUserSteamId, toUserSteamId }),
    }).then(function (apiResult) {
      return apiResult.json().then(function (data) {
        if (data.ok) {
          dispatch({
            type: types.LOBBY_DECLINE_FRIEND_REQUEST,
            friendRequestDeclined: true,
          });
          getFriendRequests(toUserSteamId, dispatch);
          getPendingRequests(toUserSteamId)(dispatch);
        } else {
          dispatch({
            type: types.LOBBY_DECLINE_FRIEND_REQUEST,
            friendRequestDeclined: false,
          });
        }
        return data;
      });
    });
  };
}

export function cancelFriendRequest({ friendRequestId, recipientSteamId, userId, userSteamId }) {
  return (dispatch) => {
    // If we have friendRequestId, use the existing logic for friend requests list
    if (friendRequestId) {
      dispatch({
        type: types.LOBBY_CANCEL_FRIEND_REQUEST,
        friendRequestId: friendRequestId,
      });
    } 
    // If we have steamId parameters, make an API call to cancel the request
    else if (recipientSteamId && userId && userSteamId) {
      return fetch(`${basePath}/api/v1/lobby/cancel-friend-request/${userId}`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ recipientSteamId, userSteamId }),
      }).then(function (apiResult) {
        return apiResult.json().then(function (data) {
          if (data.ok) {
            console.log("Friend request cancelled!", data);
            
            // Update the profile relationship status back to none
            dispatch({
              type: types.GET_PROFILE_RELATIONSHIP,
              profileRelationship: 'none',
              viewerSteamId: userSteamId,
              targetSteamId: recipientSteamId,
            });
            getPendingRequests(userSteamId)(dispatch);
          }
          return data;
        });
      }).catch(function (error) {
        console.error("Error cancelling friend request:", error);
        throw error;
      });
    }
  };
}

export function getFriends({ userId, steamId }) {
  return (dispatch) => {
    if (!userId || !steamId) {
      console.warn("[Lobby/API] Skipping friends fetch because userId or steamId is missing", {
        userId: userId || null,
        steamId: steamId || null,
      });
      dispatch({
        type: types.LOBBY_GET_FRIENDS,
        friends: [],
      });
      return;
    }

    fetchJsonWithDiagnostics(
      `${basePath}/api/v1/lobby/friendlist/get-friends/${userId}`,
      {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ steamId }),
      },
      {
        context: "Lobby friends list",
        extra: { userId, steamId },
        fetchImpl: fetch,
      }
    ).then(function (result) {
      const data = result.data || {};

      if (result.ok && data.ok) {
        const normalizedFriends = Array.isArray(data.friends)
          ? data.friends.filter((friend) => {
              const hasFriendData = !!friend?.friendData;

              if (!hasFriendData) {
                console.warn("[Lobby/API] Ignoring friend entry without friendData", {
                  friendSteamId: friend?.friendSteamId || null,
                  userId,
                });
              }

              return hasFriendData;
            })
          : [];

        dispatch({
          type: types.LOBBY_GET_FRIENDS,
          friends: normalizedFriends.map((friend) => ({
            ...friend,
          })),
        });
      } else {
        dispatch({
          type: types.LOBBY_GET_FRIENDS,
          friends: [],
        });
      }
    });
  };
}

export function updateFriendStatus({ friendSteamId, isOnline, playerStatus, lastOnlineUpdate }) {
  return {
    type: types.LOBBY_UPDATE_FRIEND_STATUS,
    friendSteamId,
    isOnline,
    playerStatus,
    lastOnlineUpdate,
  };
}

export function updateLobbyMemberStatus({ userId, isOnline, playerStatus, lastOnlineUpdate }) {
  return {
    type: types.LOBBY_UPDATE_MEMBER_STATUS,
    userId,
    isOnline,
    playerStatus,
    lastOnlineUpdate,
  };
}

export function deleteFriend({ mySteamId, friendSteamId, userId }) {
  return (dispatch) => {
    return fetch(`${basePath}/api/v1/lobby/friendlist/delete-friend/${userId}`, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ mySteamId, friendSteamId }),
    }).then(function (apiResult) {
      return apiResult.json().then(function (data) {
        console.log(data);
        if (data.ok) {
          dispatch({
            type: types.LOBBY_DELETE_FRIEND,
            friendSteamId: data.friendToDelete,
            friendDeleted: true,
          });
        } else {
          dispatch({
            type: types.LOBBY_DELETE_FRIEND,
            friendSteamId: null,
            friendDeleted: false,
          });
        }
        return data;
      });
    });
  };
}

export function openConfirmDialog({ which, friend }) {
  return {
    type: types.LOBBY_OPEN_CONFIRM_DIALOG,
    which,
    friend,
  };
}

export function closeConfirmDialog() {
  return {
    type: types.LOBBY_CLOSE_CONFIRM_DIALOG,
  };
}

export function openJoinLobbyDialog() {
  return {
    type: types.LOBBY_OPEN_JOIN_LOBBY_DIALOG,
  };
}

export function closeJoinLobbyDialog() {
  return {
    type: types.LOBBY_CLOSE_JOIN_LOBBY_DIALOG,
  };
}

export function startInvite({ inviteData }) {
  return (dispatch) => {
    dispatch({
      type: types.LOBBY_INVITE_START,
      inviteData,
    });
  };
}

export function inviteSent(guestId) {  
  return {
    type: types.LOBBY_INVITE_SENT,
    guestId,
  };
}

export function inviteExpired(guestId) {
  return {
    type: types.LOBBY_INVITE_EXPIRED,
    guestId,
  };
}

export function inviteDeclined(guestId) {
  return {
    type: types.LOBBY_INVITE_DECLINED,
    guestId,
  };
}

export function inviteAccepted(guestId) {
  return {
    type: types.LOBBY_INVITE_ACCEPTED,
    guestId,
  };
}

export function inviteCancelled({ guestId, inviteId, fromUserId } = {}) {
  return {
    type: types.LOBBY_INVITE_CANCELLED,
    guestId,
    inviteId,
    fromUserId,
  };
}

export function createLobby(lobby){
  return {
    type: types.LOBBY_CREATE_NEW_LOBBY,
    lobby,
  };
}

export function updateLobbyWithNewPlayer(lobby){
  return {
    type: types.LOBBY_UPDATE_ADDED_NEW_PLAYER,
    lobby,
  };
}

export function lobbyPlayerLeft(lobbyCode, leftUserId, hostChanged, newHostId, totalPlayers){
  return {
    type: types.LOBBY_PLAYER_LEFT,
    lobbyCode,
    leftUserId,
    hostChanged,
    newHostId,
    totalPlayers,
  };
}

export function leaveLobby(){
  return {
    type: types.LOBBY_LEAVE_LOBBY,
  };
}

export function kickPlayerFromLobby(playerToKickId){
  return {
    type: types.LOBBY_KICK_PLAYER_FROM_LOBBY,
    playerToKickId,
  };
}

export function notifyPlayerHasBeenKicked(message){
  return function (dispatch){
    dispatch({
      type: types.LOBBY_NOTIFY_PLAYER_HAS_BEEN_KICKED,
      message,
    });

    toast.error(message || "You were kicked from the lobby", { durationMs: 4000 });

    setTimeout(() => {
      dispatch({
        type: types.LOBBY_HIDE_PLAYER_KICKED_NOTIFICATION
      });
    }, 4000);
  };
}

export function getLobbyByUserId(userId){
  return function (dispatch) {
    fetch(`${basePath}/api/v1/lobby/get-lobby-by-user-id/${userId}`, { credentials: "include" })
      .then(function (apiResult) {
        return apiResult.json().then(function (data) {
          if (data.ok) {
            console.log("Lobby found for user:", data.lobby);
            dispatch({ 
              type: types.LOBBY_GET_LOBBY_BY_USER_ID, 
              lobby: data.lobby 
            });
          } else {
            dispatch({ 
              type: types.LOBBY_GET_LOBBY_BY_USER_ID, 
              lobby: null 
            });
          }
        });
      });
  };
}

export function showPlayerJoinedNotification(nickname) {
  return function (dispatch) {
    const name = String(nickname || "Player").trim() || "Player";
    toast.success(`${name} joined the lobby!`, { durationMs: 4000 });

    dispatch({
      type: types.LOBBY_SHOW_PLAYER_JOINED_NOTIFICATION,
      nickname: name,
    });

    setTimeout(() => {
      dispatch({
        type: types.LOBBY_HIDE_PLAYER_JOINED_NOTIFICATION
      });
    }, 4000);
  };
}

export function showPlayerLeftNotification(nickname) {
  return function (dispatch) {
    const name = String(nickname || "Player").trim() || "Player";
    toast.info(`${name} left the lobby!`, { durationMs: 4000 });

    dispatch({
      type: types.LOBBY_SHOW_PLAYER_LEFT_NOTIFICATION,
      nickname: name,
    });

    setTimeout(() => {
      dispatch({
        type: types.LOBBY_HIDE_PLAYER_LEFT_NOTIFICATION
      });
    }, 4000);
  };
}

export function hidePlayerJoinedNotification() {
  return {
    type: types.LOBBY_HIDE_PLAYER_JOINED_NOTIFICATION
  };
}

export function clearInviteError(guestId) {
  return {
    type: types.LOBBY_CLEAR_INVITE_ERROR,
    guestId,
  };
}

export function searchUsers({
  q = "",
  limit = 10,
  offset = 0,
  filters = {},
  includeTotal = true,
  quick = false,
  requestId = null,
  signal,
  scope = "directory",
} = {}) {
  return function (dispatch) {
    const append = offset > 0;
    const isGlobalSearch = scope === "global";
    const loadingType = isGlobalSearch ? types.GLOBAL_PLAYER_SEARCH_LOADING : types.LOBBY_SEARCH_USERS_LOADING;
    const resultsType = isGlobalSearch ? types.GLOBAL_PLAYER_SEARCH_RESULTS : types.LOBBY_SEARCH_USERS_RESULTS;
    dispatch({ type: loadingType, offset, append, requestId });

    const params = new URLSearchParams();
    params.set("q", q || "");
    params.set("limit", String(limit));
    params.set("offset", String(offset));
    params.set("includeTotal", includeTotal ? "true" : "false");
    if (quick) params.set("quick", "true");

    if (filters.region) {
      params.set("region", filters.region);
    }
    if (filters.minRating != null) {
      params.set("minRating", String(filters.minRating));
    }
    if (filters.verifiedOnly) {
      params.set("verifiedOnly", "true");
    }
    if (filters.onlineOnly) {
      params.set("onlineOnly", "true");
    }

    const url = `${basePath}/api/v1/lobby/search-users?${params.toString()}`;
    return fetch(url, { credentials: "include", signal })
      .then(function (apiResult) {
        return apiResult.json().then(function (data) {
          if (data.ok) {
            dispatch({
              type: resultsType,
              users: data.users || [],
              total: data.total ?? 0,
              offset,
              append,
              requestId,
            });
          } else {
            dispatch({
              type: resultsType,
              users: append ? undefined : [],
              total: append ? undefined : 0,
              offset: append ? undefined : 0,
              append,
              error: data.error || data.message || "Search failed",
              requestId,
            });
          }
        });
      })
      .catch(function (error) {
        if (error?.name === "AbortError") {
          return { ok: false, aborted: true };
        }
        dispatch({
          type: resultsType,
          users: append ? undefined : [],
          total: append ? undefined : 0,
          offset: append ? undefined : 0,
          append,
          error: "Search failed",
          requestId,
        });
        return { ok: false, error: "Search failed" };
      });
  };
}

export function clearUserSearchResults() {
  return { 
    type: types.LOBBY_CLEAR_SEARCH_USERS 
  };
}

export function clearGlobalPlayerSearchResults() {
  return { type: types.GLOBAL_PLAYER_SEARCH_CLEAR };
}

export function setFriendSidebarOpen(open) {
  return { 
    type: types.LOBBY_FRIEND_SIDEBAR_SET_OPEN, 
    open 
  };
}

export function setFriendSidebarTab(tab) {
  return { 
    type: types.LOBBY_FRIEND_SIDEBAR_SET_TAB, 
    tab 
  };
}

export function setLobbySettingsMenuOpen(open) {
  return { 
    type: types.LOBBY_SETTINGS_MENU_SET_OPEN, 
    open 
  };
}

export function showUnavailableActionMessage() {
  return function (dispatch) {
    toast.warning("Not available during match searching", { durationMs: 4000 });

    dispatch({
      type: types.SHOW_UNVAILABLE_ACTION_MESSAGE,
    });

    setTimeout(() => {
      dispatch({
        type: types.HIDE_UNVAILABLE_ACTION_MESSAGE
      });
    }, 4000);
  }
}

export function showQueueErrorMessage(message) {
  return function (dispatch) {
    const text = String(message || "Failed to join matchmaking queue.").trim();
    toast.error(text, { durationMs: 5000 });

    dispatch({
      type: types.SHOW_QUEUE_ERROR_MESSAGE,
      message: text,
    });

    setTimeout(() => {
      dispatch({
        type: types.HIDE_QUEUE_ERROR_MESSAGE,
      });
    }, 5000);
  };
}

export function hideQueueBlockedModal() {
  return {
    type: types.HIDE_QUEUE_BLOCKED_MODAL,
  };
}

export function notifyMatchmakingStarted({ playerQueueActionStartTs } = {}) {
  return {
    type: types.NOTIFY_MATCHMAKING_STARTED,
    playerQueueActionStartTs,
  };
}

export function resetMatchmakingStarted() {
  return {
    type: types.RESET_MATCHMAKING_STARTED,
  };
}

export function updateQueueStatus({ playersInQueue, playersNeeded, algorithmEnabled, hint, estimatedWaitSeconds, smallerMatchOffer }) {
  return {
    type: types.UPDATE_QUEUE_STATUS,
    playersInQueue,
    playersNeeded,
    algorithmEnabled,
    hint,
    estimatedWaitSeconds,
    smallerMatchOffer,
  };
}

export function clearQueueStatus() {
  return {
    type: types.CLEAR_QUEUE_STATUS,
  };
}

export function setPlatformStats({ serversOnline, playersSearching, activeMatches }) {
  return {
    type: types.SET_PLATFORM_STATS,
    serversOnline,
    playersSearching,
    activeMatches,
  };
}

export function lobbyCodeNotFound() {
  return function (dispatch) {
    dispatch({
      type: types.LOBBY_CODE_NOT_FOUND,
      lobbyCodeNotFound: true,
    });

    setTimeout(() => {
      dispatch({
        type: types.LOBBY_CODE_NOT_FOUND,
        lobbyCodeNotFound: false,
      });
    }, 2000);
  }
}

export function lobbyFull() {
  return function (dispatch) {
    dispatch({
      type: types.LOBBY_FULL,
      lobbyFull: true,
    });

    setTimeout(() => {
      dispatch({
        type: types.LOBBY_FULL,
        lobbyFull: false,
      });
    }, 2000);
  }
}

export function updateReportPlayerState({ offenses, remarks }) {
  return {
    type: types.REPORT_PLAYER_UPDATE,
    offenses,
    remarks
  };
}

export function submitPlayerReport({ reportedSteamId, offenses, remarks, matchId }) {
  return function (dispatch) {
    return fetch(`${basePath}/api/v1/match/report-player`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        reportedSteamId,
        offenses,
        remarks,
        matchId
      })
    }).then(function (apiResult) {
      return apiResult
        .json()
        .catch(() => ({}))
        .then(function (data) {
          if (apiResult.ok && data.ok) {
            dispatch({ type: types.REPORT_PLAYER_SUBMIT_SUCCESS });
          }
          return { ok: apiResult.ok && data.ok === true, ...data, httpStatus: apiResult.status };
        });
    }).catch(function (error) {
      return { ok: false, message: error?.message || "Network error" };
    });
  };
}

export function clearReportPlayerState() {
  return {
    type: types.REPORT_PLAYER_CLEAR
  };
}

export function clearMatchData() {
  return (dispatch) => {
    stopMatchDataPolling();
    stopScoreboardPolling();
    dispatch({
      type: types.CLEAR_MATCH_DATA
    });
  };
}

// ── Membership Actions ──

export function fetchMembershipStatus(userId) {
  return function (dispatch) {
    fetch(`${basePath}/api/v1/membership/status/${userId}`, {
      credentials: "include",
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.ok && data.membership) {
          dispatch({
            type: types.SET_MEMBERSHIP_STATUS,
            membership: data.membership,
          });
        }
      })
      .catch((err) => {
        console.error("[fetchMembershipStatus] error:", err);
      });
  };
}

export function setMembershipStatus(membership) {
  return {
    type: types.SET_MEMBERSHIP_STATUS,
    membership,
  };
}

export function setSelectedServerLocations(locations) {
  return {
    type: types.SET_SELECTED_SERVER_LOCATIONS,
    locations,
  };
}

export function setServerLocations(locations) {
  return {
    type: types.SET_SERVER_LOCATIONS,
    locations,
  };
}

export function fetchServerLocations() {
  return async (dispatch, getState) => {
    try {
      const catalogState = getState()?.catalog || getState()?.catalogReducer || {};
      const candidateUrls = Array.from(
        new Set([
          `${basePath}/api/v7/server-locations`,
          "/edge/api/v7/server-locations",
          "/api/v7/server-locations",
        ])
      );

      let data = null;

      for (const url of candidateUrls) {
        try {
          const response = await fetch(url, { credentials: "include" });
          if (!response.ok) continue;
          const json = await response.json();
          if (json.ok && Array.isArray(json.locations)) {
            data = json;
            break;
          }
        } catch (err) {
          console.warn(`[fetchServerLocations] failed for ${url}:`, err.message);
        }
      }

      if (data?.ok && Array.isArray(data.locations)) {
        const locations = data.locations;
        const locationsWithIds = locations
          .map((location) => ({
            location,
            id: normalizeLocationId(location?.id ?? location?.awsRegion),
          }))
          .filter(({ id }) => Boolean(id));
        const locationIds = locationsWithIds.map(({ id }) => id);
        const restrictToArea =
          data.restrictServerLocationsToArea !== false &&
          catalogState?.matchmakingOptions?.restrictServerLocationsToArea !== false;
        const defaultLocationIds = locationsWithIds
          .filter(({ location }) => !restrictToArea || location.isInArea !== false)
          .map(({ id }) => id);
        const allowedSelectionIds = restrictToArea ? defaultLocationIds : locationIds;
        const allowedSelectionSet = new Set(allowedSelectionIds);

        const currentSelection = (catalogState?.selectedServerLocations || [])
          .map((id) => normalizeLocationId(id))
          .filter(Boolean);

        const validSelections = currentSelection.filter((id) =>
          allowedSelectionSet.has(id),
        );

        dispatch(setServerLocations(locations));

        // Trim stale/out-of-area IDs; never expand a valid curated subset back to all regions.
        if (currentSelection.length === 0 || validSelections.length !== currentSelection.length) {
          const nextSelection =
            validSelections.length > 0
              ? validSelections
              : defaultLocationIds.length > 0
                ? defaultLocationIds
                : locationIds;
          if (nextSelection.length > 0) {
            dispatch(setSelectedServerLocations(nextSelection));
          }
        }
      } else {
        console.error("[fetchServerLocations] no server-locations endpoint returned a valid payload");
      }
    } catch (err) {
      console.error("[fetchServerLocations] failed:", err.message);
    }
  };
}

// ---------------------------------------------------------------------------
// CS Skills (Tactical Profile)
// ---------------------------------------------------------------------------

export function getCsSkillsTemplate() {
  return function (dispatch) {
    fetch(`${basePath}/api/v1/cs-skills/template`)
      .then((r) => r.json())
      .then((data) => {
        if (data.ok && data.template) {
          dispatch({ type: types.CS_SKILLS_TEMPLATE_LOADED, template: data.template });
        }
      })
      .catch((err) => console.error("[getCsSkillsTemplate]", err.message));
  };
}

export function getCsSkills({ userId }) {
  return function (dispatch) {
    fetch(`${basePath}/api/v1/cs-skills/${userId}`, { credentials: "include" })
      .then((r) => r.json())
      .then((data) => {
        if (data.ok) {
          dispatch({ type: types.CS_SKILLS_LOADED, csSkills: data.csSkills });
        }
      })
      .catch((err) => console.error("[getCsSkills]", err.message));
  };
}

export function saveCsSkills({ userId, csSkills }) {
  return function (dispatch) {
    return fetch(`${basePath}/api/v1/cs-skills/${userId}`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ csSkills }),
    })
      .then((r) => r.json())
      .then((data) => {
        if (data.ok) {
          dispatch({ type: types.CS_SKILLS_SAVED, csSkills: data.csSkills });
        }
        return data;
      })
      .catch((err) => {
        console.error("[saveCsSkills]", err.message);
        return { ok: false, error: err.message };
      });
  };
}

// ---------------------------------------------------------------------------
// Latest Matches feed
// ---------------------------------------------------------------------------

export function getLatestMatches({ statusFilter = "all", limit = 40 } = {}) {
  return (dispatch) => {
    dispatch({ type: types.LATEST_MATCHES_LOAD_START });

    const qs = `status=${encodeURIComponent(statusFilter)}&limit=${encodeURIComponent(limit)}`;

    return fetchJsonWithDiagnostics(
      `${basePath}/api/gc/public/matches/latest?${qs}`,
      { credentials: "include" },
      { context: "Latest matches feed", fetchImpl: fetch }
    ).then((result) => {
      const data = result.data || {};

      if (result.ok && data.ok) {
        dispatch({
          type: types.LATEST_MATCHES_LOAD_SUCCESS,
          matches: data.matches || [],
          serverTimeMs: data.serverTimeMs || Date.now(),
        });
      } else {
        dispatch({
          type: types.LATEST_MATCHES_LOAD_ERROR,
          error: data.error || "Failed to load latest matches",
        });
      }
    }).catch((err) => {
      dispatch({
        type: types.LATEST_MATCHES_LOAD_ERROR,
        error: err?.message || "Failed to load latest matches",
      });
    });
  };
}

export function getLatestMatchesWithPolling({ statusFilter = "all", limit = 40, intervalMs = 12000 } = {}) {
  return (dispatch) => {
    stopLatestMatchesPollingTimer();
    latestMatchesPollingStatusFilter = statusFilter;

    dispatch(getLatestMatches({ statusFilter, limit }));

    latestMatchesPollingPid = setInterval(() => {
      dispatch(getLatestMatches({ statusFilter: latestMatchesPollingStatusFilter, limit }));
    }, intervalMs);
  };
}
